import React, { useState, useCallback, useEffect, useMemo } from 'react';
import Map from './components/Map';
import LoginScreen from './components/LoginScreen';
import StatisticsModal from './components/StatisticsModal';
import DateButton from './components/DateButton';
import CustomDatePicker from './components/CustomDatePicker';
import RondaButton from './components/RondaButton';
import RondaModal from './components/RondaModal';
import EventsToggleButton from './components/EventsToggleButton';
import ReportsButton from './components/ReportsButton';
import { useWebSocket } from './hooks/useWebSocket';
import { useEventData } from './hooks/useEventData';
import { useRondaRoute } from './hooks/useRondaRoute';
import { authApi, statisticsApi } from './services/api';
import './App.css';

function App() {
  // Authentication state
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);

  // Calculate default date: today - 1 day
  const getDefaultDate = () => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    // Format using local date parts - toISOString() converts to UTC and
    // can shift the date back one day for timezones ahead of UTC
    return `${yesterday.getFullYear()}-${String(yesterday.getMonth() + 1).padStart(2, '0')}-${String(yesterday.getDate()).padStart(2, '0')}`;
  };

  // Validate date format (YYYY-MM-DD) and ensure it's not in the future
  const validateDate = (dateString) => {
    if (!dateString) return null;
    
    // Check format YYYY-MM-DD
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (!dateRegex.test(dateString)) return null;
    
    // Parse and validate date
    const date = new Date(dateString + 'T00:00:00');
    if (isNaN(date.getTime())) return null;
    
    // Check if date is not in the future
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (date > today) return null;
    
    return dateString;
  };
  
  // Check URL for ronda and data parameters to determine initial state
  const getInitialState = () => {
    const searchParams = new URLSearchParams(window.location.search);
    const rondaParam = searchParams.get('ronda');
    const dataParam = searchParams.get('data');
    const validatedDate = validateDate(dataParam);
    
    return {
      mode: rondaParam ? 'byRonda' : 'byDate',
      ronda: rondaParam || null,
      date: validatedDate
    };
  };
  
  const initialState = getInitialState();
  
  // App state - simplified to mode, selectedDate, selectedRonda
  const [mode, setMode] = useState(initialState.mode);
  const [selectedDate, setSelectedDate] = useState(initialState.date || getDefaultDate());
  const [selectedRonda, setSelectedRonda] = useState(initialState.ronda);
  // Keep track of last selected ronda to restore when switching back to byRonda mode
  const [lastSelectedRonda, setLastSelectedRonda] = useState(initialState.ronda);

  // Modal states
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showRondaModal, setShowRondaModal] = useState(false);
  const [showStatsModal, setShowStatsModal] = useState(false);
  const [statsData, setStatsData] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsError, setStatsError] = useState(null);

  // Check authentication on app load
  useEffect(() => {
    let isMounted = true;

    const checkAuth = async () => {
      setAuthLoading(true);

      try {
        if (authApi.isAuthenticated()) {
          const user = authApi.getCurrentUser();
          if (user) {
            if (isMounted) {
              setIsAuthenticated(true);
            }
          } else {
            // Token exists but no user data, clear and show login
            authApi.clearAuth();
            if (isMounted) {
              setIsAuthenticated(false);
            }
          }
        } else {
          if (isMounted) {
            setIsAuthenticated(false);
          }
        }
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Auth check error:', error);
        }
        authApi.clearAuth();
        if (isMounted) {
          setIsAuthenticated(false);
        }
      } finally {
        if (isMounted) {
          setAuthLoading(false);
        }
      }
    };

    checkAuth();

    return () => {
      isMounted = false;
    };
  }, []);

  // No need to update date in useEffect - it's already set in initial state

  // Authentication handlers
  const handleLoginSuccess = useCallback((user, tokens) => {
    // Verify that tokens were stored (LoginScreen handles storage)
    if (!authApi.isAuthenticated()) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('Login succeeded but token not found in storage');
      }
      return;
    }

    // Verify user data
    if (!user || !user.email) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('Login succeeded but invalid user data');
      }
      authApi.clearAuth();
      return;
    }

    setIsAuthenticated(true);
  }, []);

  // Initialize event data hook - fetch events for selected date
  // Only fetch after authentication (endpoints require auth)
  const {
    events,
    loading: eventsLoading,
    error: eventsError,
    addEvent
  } = useEventData(selectedDate, isAuthenticated);

  // Filter events based on mode and selected ronda
  const filteredEvents = useMemo(() => {
    // Mode byDate = show all events
    if (mode === 'byDate') {
      return events;
    }

    // Mode byRonda without selected ronda = no events
    if (!selectedRonda) {
      return [];
    }

    // Mode byRonda with ronda = filter events for that ronda
    return events.filter(e => e.ronda_id === selectedRonda);
  }, [events, mode, selectedRonda]);

  // Initialize ronda route hook - calculate route via OSRM when ronda is selected
  const {
    routePoints,
    currentPosition,
    rondaInfo,
    loading: routeLoading,
    rondaEvents,
    startAnimation
  } = useRondaRoute(events, selectedRonda);
  
  // Debug: Log when ronda is set from URL
  useEffect(() => {
    if (selectedRonda && process.env.NODE_ENV === 'development') {
      console.log('Ronda selected from URL:', selectedRonda, 'Date:', selectedDate, 'Mode:', mode);
    }
  }, [selectedRonda, selectedDate, mode]);

  // WebSocket handlers - simplified
  const handleVolunteerLogin = useCallback((loginData) => {
    if (process.env.NODE_ENV === 'development') {
      console.log('Volunteer logged in:', loginData);
    }
    // Could show notification or update UI here
  }, []);

  const handleVolunteerLogoutWS = useCallback((logoutData) => {
    if (process.env.NODE_ENV === 'development') {
      console.log('Volunteer logged out:', logoutData);
    }
    // Could show notification or update UI here
  }, []);

  const handleEventRegistered = useCallback((eventData) => {
    if (process.env.NODE_ENV === 'development') {
      console.log('Event registered:', eventData);
    }

    // Validate event data before processing
    if (!eventData || !eventData.event_id || !eventData.latitude || !eventData.longitude) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('Invalid event data received:', eventData);
      }
      return;
    }

    // Convert WebSocket event data to our event format
    const newEvent = {
      id: eventData.event_id,
      ronda_id: eventData.ronda_id || eventData.ronda_codigo || '',
      location: {
        latitude: eventData.latitude,
        longitude: eventData.longitude
      },
      entity: {
        name: eventData.entity_name || 'Desconhecida'
      },
      tipo: eventData.tipo || 'outro',
      local: eventData.local || '',
      pessoas_assistidas: eventData.pessoas_assistidas || eventData.pessoas_presentes || 0,
      numero_refeicoes: eventData.numero_refeicoes || 0,
      ronda_name: eventData.ronda_name || '',
      created_at: eventData.timestamp || new Date().toISOString()
    };
    addEvent(newEvent);
  }, [addEvent]);

  // Initialize WebSocket
  const { isConnected, connectionError } = useWebSocket(
    null, // No longer using location_update
    handleVolunteerLogin,
    handleVolunteerLogoutWS,
    handleEventRegistered
  );

  // Log WebSocket connection status (only in development)
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      if (connectionError) {
        console.warn('WebSocket connection error:', connectionError);
      } else if (isConnected) {
        console.log('WebSocket connected');
      }
    }
  }, [isConnected, connectionError]);

  // Log errors (only in development) - UI can show these if needed
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      if (eventsError) {
        console.error('Events error:', eventsError);
      }
    }
  }, [eventsError]);

  // Handlers
  const handleDateChange = useCallback((newDate) => {
    setSelectedDate(newDate);
    // Clear ronda selection when date changes
    setSelectedRonda(null);
  }, []);

  const handleRondaSelect = useCallback((rondaCodigo) => {
    setSelectedRonda(rondaCodigo);
    setLastSelectedRonda(rondaCodigo); // Remember this ronda
    // When ronda is selected, switch to byRonda mode
    setMode('byRonda');
  }, []);

  const handleEventsToggle = useCallback(() => {
    // Toggle between byDate and byRonda modes
    if (mode === 'byRonda') {
      // Switch to events mode (byDate)
      // Don't clear selectedRonda - just hide the track, keep it for when switching back
      setMode('byDate');
    } else {
      // Switch to ronda tracking mode (byRonda)
      setMode('byRonda');
      // Restore last selected ronda if available, otherwise keep current selectedRonda
      if (lastSelectedRonda && !selectedRonda) {
        setSelectedRonda(lastSelectedRonda);
      }
    }
  }, [mode, lastSelectedRonda, selectedRonda]);

  // Date picker handler - always use custom picker
  const handleOpenDatePicker = useCallback(() => {
    setShowDatePicker(true);
  }, []);
  
  const handleCloseDatePicker = useCallback(() => {
    setShowDatePicker(false);
  }, []);

  const handleOpenRondaModal = useCallback(() => {
    setShowRondaModal(true);
  }, []);

  const handleCloseRondaModal = useCallback(() => {
    setShowRondaModal(false);
  }, []);

  const handleOpenReportsModal = useCallback(async () => {
    // Prevent multiple simultaneous calls
    if (statsLoading) {
      return;
    }

    setShowStatsModal(true);
    setStatsLoading(true);
    setStatsError(null);
    setStatsData(null);

    try {
      const response = await statisticsApi.getStatistics({
        data_inicio: selectedDate,
        data_fim: selectedDate
      });

      // Validate response data
      const statsData = response?.data?.data;
      if (Array.isArray(statsData)) {
        setStatsData(statsData);
      } else {
        setStatsData([]);
        if (process.env.NODE_ENV === 'development') {
          console.warn('Unexpected statistics response format:', response.data);
        }
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error fetching statistics:', error);
      }

      // Provide user-friendly error messages
      let errorMessage = 'Erro ao carregar estatísticas';
      if (error.isNetworkError) {
        errorMessage = 'Erro de conexão. Verifique sua internet.';
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      setStatsError(errorMessage);
    } finally {
      setStatsLoading(false);
    }
  }, [selectedDate, statsLoading]);

  const handleCloseStatsModal = useCallback(() => {
    setShowStatsModal(false);
    setStatsData(null);
    setStatsError(null);
  }, []);

  // Show loading while checking authentication
  if (authLoading) {
    return (
      <div className="app-loading">
        <div className="loading-spinner"></div>
        <p>Carregando...</p>
      </div>
    );
  }

  // Show login screen if not authenticated
  if (!isAuthenticated) {
    return <LoginScreen onLoginSuccess={handleLoginSuccess} />;
  }

  return (
    <div className="app">
      <Map
        events={filteredEvents}
        routePoints={selectedRonda && mode === 'byRonda' ? routePoints : []}
        currentPosition={selectedRonda && mode === 'byRonda' ? currentPosition : null}
        rondaInfo={selectedRonda && mode === 'byRonda' ? rondaInfo : null}
        selectedDate={selectedDate}
        onVehicleClick={selectedRonda && mode === 'byRonda' ? startAnimation : null}
      />

      {/* Floating Action Buttons */}
      <a href="/dash" className="dashboard-button" title="Dashboard">
        📊
      </a>
      <EventsToggleButton 
        onClick={handleEventsToggle} 
        isActive={mode === 'byDate'}
      />
      <DateButton onClick={handleOpenDatePicker} />
      <RondaButton onClick={handleOpenRondaModal} />
      <ReportsButton onClick={handleOpenReportsModal} />

      {/* Custom date picker - works on both desktop and mobile */}
      <CustomDatePicker
        isOpen={showDatePicker}
        onClose={handleCloseDatePicker}
        selectedDate={selectedDate}
        onDateChange={handleDateChange}
      />

      {/* Ronda Modal */}
      <RondaModal
        isOpen={showRondaModal}
        onClose={handleCloseRondaModal}
        selectedDate={selectedDate}
        selectedRonda={selectedRonda}
        onRondaSelect={handleRondaSelect}
      />

      {/* Statistics Modal */}
      <StatisticsModal
        isOpen={showStatsModal}
        onClose={handleCloseStatsModal}
        date={selectedDate}
        data={statsData}
        loading={statsLoading}
        error={statsError}
      />

      {/* Loading Overlay */}
      {(eventsLoading || routeLoading) && (
        <div className="loading">
          Carregando dados...
        </div>
      )}
    </div>
  );
}

export default App;